import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import IndustryHero from './components/IndustryHero';
import IndustryCard from './components/IndustryCard';
import CaseStudyCard from './components/CaseStudyCard';
import IndustryStats from './components/IndustryStats';
import IndustryExpertise from './components/IndustryExpertise';
import IndustryTestimonials from './components/IndustryTestimonials';
import IndustryCTA from './components/IndustryCTA';

const Industries = () => {
  const industries = [
  {
    id: 1,
    name: "Logistics & Supply Chain",
    icon: "Truck",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18cbf0375-1766522226918.png",
    imageAlt: "Modern warehouse facility with automated conveyor systems and organized inventory shelves under bright industrial lighting",
    description: "Transform supply chain operations with intelligent 1.0v TMS, WMS, and real-time tracking solutions that optimize routes, reduce costs, and improve delivery performance.",
    expertise1: "Transportation Management Systems (TMS)",
    expertise2: "Warehouse Management Solutions (WMS)",
    expertise3: "Real-time Fleet Tracking & Analytics",
    projectCount: 15
  },
  {
    id: 2,
    name: "Healthcare & Medical",
    icon: "Heart",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14c968347-1764642437513.png",
    imageAlt: "Modern hospital corridor with medical professionals in scrubs walking past advanced medical equipment and patient care stations",
    description: "HIPAA-compliant healthcare platforms including EHR systems, patient portals, and telemedicine solutions that enhance care delivery and operational efficiency.",
    expertise1: "Electronic Health Records (EHR) Systems",
    expertise2: "Patient Engagement Portals",
    expertise3: "Telemedicine & Remote Care Platforms",
    projectCount: 15
  },
  {
    id: 3,
    name: "Financial Services & Fintech",
    icon: "DollarSign",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17cca8024-1766513023990.png",
    imageAlt: "Financial trading floor with multiple computer monitors displaying real-time market data and stock charts in modern office setting",
    description: "Secure fintech platforms with payment processing, digital banking, and wealth management solutions meeting PCI-DSS and regulatory compliance standards.",
    expertise1: "Payment Gateway Integration",
    expertise2: "Digital Banking Platforms",
    expertise3: "Regulatory Compliance & Security",
    projectCount: 18
  },
  {
    id: 4,
    name: "Manufacturing & Industrial",
    icon: "Factory",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bb9cfaf5-1766484751553.png",
    imageAlt: "Advanced manufacturing facility with robotic assembly lines and automated machinery producing precision components",
    description: "Industry 4.0 solutions including IoT integration, predictive maintenance, and production optimization systems that drive operational excellence.",
    expertise1: "IoT & Smart Factory Solutions",
    expertise2: "Predictive Maintenance Systems",
    expertise3: "Production Planning & Optimization",
    projectCount: 10
  },
  {
    id: 5,
    name: "Retail & E-Commerce",
    icon: "ShoppingCart",
    image: "https://images.unsplash.com/photo-1676473229624-f571e3cf1cc8",
    imageAlt: "Modern retail store interior with organized product displays and customers browsing merchandise under contemporary lighting",
    description: "Omnichannel retail platforms with inventory management, customer analytics, and personalized shopping experiences that drive sales growth.",
    expertise1: "E-Commerce Platform Development",
    expertise2: "Inventory & Order Management",
    expertise3: "Customer Analytics & Personalization",
    projectCount: 14
  },
  {
    id: 6,
    name: "Education & E-Learning",
    icon: "GraduationCap",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e56c0a8a-1766825006595.png",
    imageAlt: "Modern classroom with students using tablets and interactive digital displays for collaborative learning activities",
    description: "Comprehensive learning management systems, virtual classrooms, and educational content platforms that enhance student engagement and outcomes.",
    expertise1: "Learning Management Systems (LMS)",
    expertise2: "Virtual Classroom Solutions",
    expertise3: "Student Progress Analytics",
    projectCount: 8
  }];


  const caseStudies = [
  {
    id: 1,
    industry: "Logistics",
    title: "Real-Time Fleet Management Transformation",
    description: "Built comprehensive TMS platform enabling real-time tracking of 500+ vehicles, automated route optimization, and predictive maintenance scheduling.",
    image: "https://images.unsplash.com/photo-1669003153363-6d7ba8e20c7e",
    imageAlt: "Fleet of modern delivery trucks parked in organized rows at distribution center with GPS tracking devices visible",
    clientName: "GlobalFreight Solutions",
    clientLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_1472fa36f-1766850745698.png",
    clientLogoAlt: "GlobalFreight Solutions company logo featuring blue globe with transportation routes",
    metric1Value: "40%",
    metric1Label: "Faster Delivery",
    metric2Value: "35%",
    metric2Label: "Cost Reduction",
    metric3Value: "99.8%",
    metric3Label: "On-Time Rate"
  },
  {
    id: 2,
    industry: "Healthcare",
    title: "HIPAA-Compliant Patient Portal",
    description: "Developed secure patient engagement platform with appointment scheduling, medical records access, and telemedicine capabilities serving 50,000+ patients.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_195a6e902-1764648555298.png",
    imageAlt: "Healthcare professional using tablet to access digital patient records in modern medical facility",
    clientName: "HealthFirst Medical",
    clientLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_1b195a4cb-1764655048805.png",
    clientLogoAlt: "HealthFirst Medical logo with medical cross symbol in green and white colors",
    metric1Value: "65%",
    metric1Label: "Patient Engagement",
    metric2Value: "50%",
    metric2Label: "Admin Time Saved",
    metric3Value: "4.8/5",
    metric3Label: "User Rating"
  },
  {
    id: 3,
    industry: "Fintech",
    title: "Enterprise Payment Processing Platform",
    description: "Created scalable payment gateway handling $10M+ daily transactions with fraud detection, multi-currency support, and PCI-DSS compliance.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14acfbeab-1766488420530.png",
    imageAlt: "Digital payment processing interface showing secure transaction flow with encryption symbols and verification checkmarks",
    clientName: "PayStream Financial",
    clientLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_1eb49c237-1766850744322.png",
    clientLogoAlt: "PayStream Financial logo featuring stylized payment card with secure lock icon",
    metric1Value: "99.99%",
    metric1Label: "Uptime",
    metric2Value: "2.5s",
    metric2Label: "Avg Processing",
    metric3Value: "0.01%",
    metric3Label: "Fraud Rate"
  },
  {
    id: 4,
    industry: "Manufacturing",
    title: "Smart Factory IoT Integration",
    description: "Implemented IoT-enabled production monitoring system with predictive maintenance, quality control automation, and real-time performance dashboards.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f6c2206b-1764642435778.png",
    imageAlt: "Industrial IoT sensors and monitoring equipment installed on manufacturing machinery with digital displays showing real-time data",
    clientName: "PrecisionTech Industries",
    clientLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_19a923137-1766850746524.png",
    clientLogoAlt: "PrecisionTech Industries logo with gear and circuit board design elements",
    metric1Value: "45%",
    metric1Label: "Downtime Reduced",
    metric2Value: "30%",
    metric2Label: "Quality Improved",
    metric3Value: "$2.5M",
    metric3Label: "Annual Savings"
  },
  {
    id: 5,
    industry: "Retail",
    title: "Omnichannel E-Commerce Platform",
    description: "Built unified retail platform integrating online store, mobile app, and in-store POS with real-time inventory sync and personalized recommendations.",
    image: "https://images.unsplash.com/photo-1734825937705-d4205842a566",
    imageAlt: "Modern retail point-of-sale system with touchscreen display showing product catalog and customer shopping cart",
    clientName: "StyleHub Retail",
    clientLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_113712d0b-1766850744845.png",
    clientLogoAlt: "StyleHub Retail logo featuring shopping bag with modern typography",
    metric1Value: "85%",
    metric1Label: "Sales Increase",
    metric2Value: "3.2x",
    metric2Label: "Conversion Rate",
    metric3Value: "92%",
    metric3Label: "Customer Retention"
  },
  {
    id: 6,
    industry: "Education",
    title: "Comprehensive Learning Management System",
    description: "Developed cloud-based LMS with virtual classrooms, assignment management, progress tracking, and parent-teacher communication for 10,000+ students.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b4292620-1764671097098.png",
    imageAlt: "Student using laptop to access online learning platform with interactive course materials and video lectures",
    clientName: "EduTech Academy",
    clientLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_11c3cd641-1764664280856.png",
    clientLogoAlt: "EduTech Academy logo with graduation cap and digital book symbols",
    metric1Value: "78%",
    metric1Label: "Engagement Up",
    metric2Value: "60%",
    metric2Label: "Admin Efficiency",
    metric3Value: "4.7/5",
    metric3Label: "Teacher Rating"
  }];


  return (
    <>
      <Helmet>
        <title>Industry Solutions | Devontis Technologies - Vertical-Specific SaaS Development</title>
        <meta name="description" content="Specialized SaaS development expertise across logistics, healthcare, fintech, manufacturing, retail, and education. Deep domain knowledge meets technical excellence." />
        <meta name="keywords" content="industry solutions, vertical SaaS, logistics software, healthcare platforms, fintech development, manufacturing systems" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <IndustryHero />
          
          <section className="py-12 md:py-16 lg:py-20 bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* <Breadcrumbs /> */}
              
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-headline text-foreground mb-4">
                  Industries We Serve
                </h2>
                <p className="text-base md:text-lg text-muted-foreground font-body max-w-3xl mx-auto">
                  Specialized expertise across complex industries with proven track records of delivering transformative solutions
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {industries?.map((industry) =>
                <IndustryCard key={industry?.id} industry={industry} />
                )}
              </div>
            </div>
          </section>

          <IndustryStats />

          <section className="py-12 md:py-16 lg:py-20 bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium font-body mb-4">
                  <span>Success Stories</span>
                </div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-headline text-foreground mb-4">
                  Industry Transformation Case Studies
                </h2>
                <p className="text-base md:text-lg text-muted-foreground font-body max-w-3xl mx-auto">
                  Real-world examples of how we've helped industry leaders achieve measurable business outcomes
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {caseStudies?.map((caseStudy) =>
                <CaseStudyCard key={caseStudy?.id} caseStudy={caseStudy} />
                )}
              </div>
            </div>
          </section>

          <IndustryExpertise />
          <IndustryTestimonials />
          <IndustryCTA />
        </main>

        <Footer />
      </div>
    </>);

};

export default Industries;